import { usePrefetchQuery, useQuery, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { maxEndppointOIDCProvidersQueryOptions } from '#api/endpoint-oidc-providers.queries.ts';
import { hostsCountQueryOptions } from '#api/hosts.queries.ts';
import { HOST_FILTER, LIGHTHOUSE_FILTER, RELAY_FILTER, type GetHostsFilter } from '#api/hosts.ts';
import { maxJoinKeysQueryOptions } from '#api/join-keys.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { ModalBody, ModalFooter } from '#components/patterns/modal/index.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { HOST_KIND_LABELS, HOST_KINDS, type HostKind } from '#util/host.ts';
import { DeleteNetworkConfirmation } from './DeleteNetworkConfirmation.tsx';

type Props = {
  networkId: string;
  onCancel: () => void;
  onDelete: () => void;
};

/**
 * The kind slice each count is scoped to. Driving both the query and the label off {@link HOST_KINDS}
 * is what keeps a count from ever disagreeing with the noun printed beside it.
 */
const KIND_FILTERS: Record<HostKind, GetHostsFilter> = {
  host: HOST_FILTER,
  lighthouse: LIGHTHOUSE_FILTER,
  relay: RELAY_FILTER,
};

// "19 hosts, 4 lighthouses, and 5 relays" — also covers the one- and two-item cases.
const kindListFormat = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/**
 * This is its own component so that the parent, {@link DeleteNetworkModal}, can have
 * a suspense boundary inside.  This means that the modal opens immediately, and we show
 * a spinner in the modal body while we determine what to show, which can be:
 *
 * - A per-kind count of the machines still in this network, linking to the network's hosts list
 * - The endpoint SSO providers that enroll hosts into this network, linking to each
 * - The join keys that enroll hosts into this network, linking to each
 * - Or a confirmation modal of "Are you sure you want to do this?" ({@link DeleteNetworkConfirmation})
 */
export function DeleteNetworkModalBody({ networkId, onCancel, onDelete }: Props) {
  // Starts the providers fetch alongside the host counts instead of after them suspend.
  usePrefetchQuery(maxEndppointOIDCProvidersQueryOptions);
  const kindCounts = useSuspenseQueries({
    // The explicit networkID scopes these to the network being deleted, never the ambient network filter.
    queries: HOST_KINDS.map((kind) => hostsCountQueryOptions({ ...KIND_FILTERS[kind], networkID: networkId })),
    // Results come back in query order, so zipping by index pairs each count with its own kind.
    combine: (results) =>
      HOST_KINDS.map((kind, index) => ({
        kind,
        count: assert(results[index], `missing host count result for ${kind}`).data,
      })),
  });

  const {
    data: { data: endpointOIDCProviders },
  } = useSuspenseQuery(maxEndppointOIDCProvidersQueryOptions);
  const providersInNetwork = endpointOIDCProviders.filter((provider) => provider.networkID === networkId);

  const joinKeysQuery = useQuery(maxJoinKeysQueryOptions);
  const joinKeysInNetwork = joinKeysQuery.data?.data.filter((joinKey) => joinKey.networkID === networkId) ?? [];

  const totalCount = kindCounts.reduce((total, { count }) => total + count, 0);

  if (joinKeysQuery.isPending) {
    return (
      <div style={{ height: 220 }}>
        <LoadingSpinner />
      </div>
    );
  }

  // If anything still depends on this network, we have to show a different modal body, pointing at
  // the network's hosts list — unfiltered by kind, because everything in it has to go — and at each provider and join key.
  if (totalCount > 0 || providersInNetwork.length > 0 || joinKeysInNetwork.length > 0) {
    const countsPhrase = kindListFormat.format(
      kindCounts.filter(({ count }) => count > 0).map(({ kind, count }) => formatKindCount(kind, count))
    );
    const networkHostsTo = `/hosts?filter.networkID=${networkId}`;

    return (
      <>
        <ModalBody>
          {totalCount > 0 && (
            <p className="p">
              You can’t delete a network that isn’t empty. This network still contains {countsPhrase} — remove{' '}
              {totalCount === 1 ? 'it' : 'them'} before deleting it.
            </p>
          )}
          {providersInNetwork.length > 0 && (
            <>
              <p className="p">
                This network is used by the following endpoint SSO provider{providersInNetwork.length === 1 ? '' : 's'}.
                Move {providersInNetwork.length === 1 ? 'it' : 'them'} to another network before deleting it:
              </p>
              <ul className="ul">
                {providersInNetwork.map((provider) => (
                  <li className="p" key={provider.id}>
                    <Link className="a" to={`/settings/sso/${provider.id}`}>
                      {provider.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {joinKeysInNetwork.length > 0 && (
            <>
              <p className="p">
                Hosts enrolled with the following join key{joinKeysInNetwork.length === 1 ? '' : 's'} join this network.
                Revoke {joinKeysInNetwork.length === 1 ? 'it' : 'them'} before deleting it:
              </p>
              <ul className="ul">
                {joinKeysInNetwork.map((joinKey) => (
                  <li className="p" key={joinKey.id}>
                    <Link className="a" to={`/settings/join-keys/${joinKey.id}`}>
                      {joinKey.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ConfirmButtonGroup
            variant="right"
            primaryButton={
              totalCount > 0 ?
                <LinkButton variant="primary" to={networkHostsTo}>
                  See this network’s hosts
                </LinkButton>
              : <Button variant="primary" onClick={onCancel}>
                  Close
                </Button>
            }
            secondaryButton={
              totalCount > 0 && (
                <Button variant="link" onClick={onCancel}>
                  Cancel
                </Button>
              )
            }
          />
        </ModalFooter>
      </>
    );
  }

  return <DeleteNetworkConfirmation networkId={networkId} onCancel={onCancel} onDelete={onDelete} />;
}

/** e.g. "1 lighthouse" / "4 lighthouses" — every host kind pluralizes with a bare `s`. */
function formatKindCount(kind: HostKind, count: number) {
  const noun = HOST_KIND_LABELS[kind].toLowerCase();
  return `${count.toLocaleString()} ${count === 1 ? noun : `${noun}s`}`;
}
